import { formatMessage, randomMessage } from "model/message";
import Debug from "debug";
import { standingChanged } from "lib/strings/city";
import clock from "util/clock";
const debug = Debug("action:work");

const action = "Work";
const skill = "work";
const value = (max) => Math.ceil(Math.random() * max);

const WORK_MAX_HEALTH_COST = 10;
const WORK_MAX_FOOD_GAIN = 8;
const WORK_MAX_COINS_GAIN = 10;
// Adding a resilience gain chance
const RESILIENCE_GAIN_CHANCE = 0.2; // 20% chance to gain resilience
const STATUS_METRICS = {
  influence: 0.2,
  intimidation: -0.5,
  respect: 1,
};

const notification = ({ gain: { resilience } }, total, newStatus) => {
  if ((!resilience || total % 10 !== 0) && !newStatus) return;

  // Worded once in lib/strings/city.js — the crossing is the town's, not this action's, and
  // beg and rob reach the same three lines.
  if (newStatus) {
    const line = standingChanged(newStatus);
    if (line) return formatMessage(line);
  }

  // Every one of these used to name a virtue — `resolve hardens`, `endurance from toil`. This
  // fires once every ten resilience, so it is the slow channel: a body that has changed since
  // the last time anyone looked at it.
  const message = randomMessage([
    "the hands have stopped blistering and started to harden.",
    "you carry the second load the same as the first.",
    "the heat does not take it out of you the way it did.",
    "wake before they call you and lie there waiting.",
    "the work is the same size. you are not.",
  ]);
  return message;
};

/**
 * Work is the one action that can say what it was.
 *
 * Rob's button renames itself up the crime ladder, so its lines have to stay clear of the
 * method. Work is the opposite: `actionLabel` is `Work` at all eleven labour levels and the
 * fiction underneath is fixed and known — it is a ranch. The promotion events name a foreman,
 * a gap in the fence, a wild stallion, branding irons, a stew the crew eats, a cattle drive
 * (skills/labour/events/en.json), and EventManager sends the player to the rancher the first
 * time they work at all. So the day is written out, and it moves as the ladder does.
 *
 * Three bands, and then the body. What you did is the band; what it cost is the body, and the
 * body does not care which job it was — a day that takes everything reads the same whether the
 * herd or a wagon took it. So the hard-day lines are shared, and they outrank the band, the
 * way an injury outranks the score in rob.
 */

// Ranch Hand. The ladder leaves the town here and goes out to the herd.
const BAND_RANCH = 2;
// Chuck Wrangler. From here the work is other people's work.
const BAND_CREW = 7;
// The top of the roll, so most days are the day's own line and not the body's.
const HARD_DAY = Math.round(WORK_MAX_HEALTH_COST * 0.8);

const early = [
  "haul and stack and haul again until the light goes.",
  "whatever wants lifting. they point and you lift it.",
  "sacks and boards and a wagon to load. nobody asks your name.",
  "the work is not particular about who does it.",
];

const ranch = [
  "post holes and wire and the herd moving off south.",
  "a day in the saddle and the dust of them in your teeth.",
  "wire strung and stapled and the gap closed.",
  "cattle in and counted and the gate wired shut.",
];

const crew = [
  "irons in the fire all day and the smell of it in your clothes.",
  "the crew fed and the pots scoured and the fire banked.",
  "you say where they go and they go there.",
  "the drive strung out for a mile and holding.",
];

const hardDays = [
  "worked past the point where it stops being work.",
  "hands shake on the walk back.",
  "the last of it done on nothing at all.",
  "nothing left in the arms by the end of it.",
];

// The day that takes the button away: `enabled` needs more than WORK_MAX_HEALTH_COST to press,
// so a day ending at or under it is the last one until he sleeps. These say the state he is
// left in rather than how hard the day was — a small day on a nearly empty man ends the run
// just as surely, and the numbers on the toast say which it was.
const spentDays = [
  "there is nothing left in you after this one.",
  "that is the last of it. the arms will not do another.",
];

const message = ({ cost, gain, level, spent }) => {
  const day = spent
    ? spentDays
    : cost.health >= HARD_DAY
    ? hardDays
    : level >= BAND_CREW
    ? crew
    : level >= BAND_RANCH
    ? ranch
    : early;

  // `resilience grows.` used to be spliced in here, an inch from the **resilience +1** that
  // format() appends to this same toast. The number says it. The log line says the rest.
  return formatMessage(randomMessage(day), { gain, cost });
};

const attempt = ({ party }) => {
  const { progression, resources } = party;
  // Retrieve the current work level from the progressionManager
  const workLevel = progression.getCurrentLevelIndex(skill);
  const levelBonus = Math.sqrt(workLevel); // Use square root to moderate the level's impact
  debug("workLevel", workLevel, levelBonus);

  // Dynamic gains based on level with a base value and an added bonus
  const foodGain = value(WORK_MAX_FOOD_GAIN) + Math.floor(levelBonus);
  const coinsGain = value(WORK_MAX_COINS_GAIN) + Math.floor(levelBonus * 2); // Coins gain might have a higher level impact

  // Dynamic health cost increases with level but has a cap
  const healthCost = Math.min(
    resources.attribs.health.value - 1,
    Math.min(
      value(WORK_MAX_HEALTH_COST) + Math.floor(levelBonus / 2),
      WORK_MAX_HEALTH_COST,
    ),
  );

  // Chance to gain resilience
  const resilienceGain = Math.random() < RESILIENCE_GAIN_CHANCE ? 1 : 0;

  const result = {
    success: true,
    // The band the day's line is drawn from, and whether this day empties him — see `message`.
    level: workLevel,
    spent: resources.attribs.health.value - healthCost <= WORK_MAX_HEALTH_COST,
    cost: {
      health: healthCost,
    },
    gain: {
      food: foodGain,
      coins: coinsGain,
      resilience: resilienceGain,
    },
  };

  return result;
};

const executeSideEffects = (context, result) => {
  const { party, calendar, political } = context;
  const { gain, cost } = result;
  const { history, resources, traits, city } = party;

  // Track action
  const workedBefore = history.total(action) > 0;
  if (!workedBefore) {
    history.delta({ rancherWork: true }, context);
    history.add({ action: "ENABLE_WORK", result });
  }
  history.add({ action, result });

  // Update resources
  resources.delta(
    {
      food: gain.food,
      coins: gain.coins,
      health: -cost.health,
    },
    context,
  );

  // Update traits
  traits.delta(
    {
      resilience: gain.resilience,
    },
    context,
  );

  // Update progression
  party.progression.addExperience(skill, 1, traits, context);

  const { status, isNewStatus } = political.updateStatusMetrics(
    city,
    STATUS_METRICS,
  );
  // Time passes
  // The player bought this phase. Rebaseline the clock so the accumulator, which may
  // already be nearly full, does not hand over a second phase moments later.
  calendar.nextStep(context);
  clock.reset();

  // notify of the results
  return {
    message: message(result),
    notification: notification(
      result,
      traits.getTraitValue("resilience"),
      isNewStatus ? status : null,
    ),
  };
};

const work = {
  title: "Work",
  hotkey: "w",
  type: action,
  level: ({ party }) => party.progression.getCurrentLevelIndex(skill),
  enabled: ({ calendar, party }) => {
    return party.resources.attribs.health.value > WORK_MAX_HEALTH_COST &&
      !calendar.isNight();
  },
  execute: (context) => {
    const result = attempt(context);
    const { message, notification, event } = executeSideEffects(
      context,
      result,
    );

    return { result: { message }, outcome: { event }, notification };
  },
  tooltips: [["health", `1 - ${WORK_MAX_HEALTH_COST}`]],
};

const workWithCallback = (notifyDone) => ({
  ...work,
  enabled: true,
  execute: (context) => {
    const result = work.execute(context);
    notifyDone();
    return result;
  },
});

export { action as WorkType, work as default, workWithCallback };
